import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({ message: z.string(), code: z.string().optional() }),
});

export const plannerResourceSchema = z.object({
  id: z.number(), // userId
  name: z.string(),
  color: z.string(),
  teamId: z.number().nullable(),
  teamName: z.string().nullable(),
  // Team's Daily Job Capacity (team.max_capacity); null = unlimited (SC-6668).
  maxCapacity: z.number().nullable(),
});

export const plannerEventSchema = z.object({
  id: z.number(), // schedule_member.id
  title: z.string(),
  start: z.string(), // ISO datetime
  end: z.string(), // ISO datetime
  userId: z.number(), // the resource
  userName: z.string(),
  scheduleId: z.number().nullable(),
  jobId: z.number().nullable(),
  teamId: z.number().nullable(),
  teamName: z.string().nullable().optional(),
  color: z.string().nullable(),
});

export const createPlannerEventBodySchema = z.object({
  title: z.string().min(1).max(200).optional(),
  start: z.string().datetime({ message: 'start must be a valid ISO 8601 datetime' }),
  end: z.string().datetime({ message: 'end must be a valid ISO 8601 datetime' }),
  userId: z.number(),
  jobId: z.number().optional(),
  teamId: z.number().optional(),
});

export const updatePlannerEventBodySchema = z.object({
  title: z.string().min(1).max(200).optional(),
  start: z.string().datetime({ message: 'start must be a valid ISO 8601 datetime' }).optional(),
  end: z.string().datetime({ message: 'end must be a valid ISO 8601 datetime' }).optional(),
  userId: z.number().optional(),
});

export const validatePlannerEventBodySchema = z.object({
  start: z.string(),
  end: z.string(),
  userId: z.number(),
  excludeId: z.number().optional(),
});

export const validatePlannerEventResponseSchema = z.object({
  hasConflict: z.boolean(),
  conflicts: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
      start: z.string(),
      end: z.string(),
    }),
  ),
});

export const plannerContract = c.router({
  getResources: {
    method: 'GET',
    path: '/resources',
    query: z.object({ teamId: z.string().optional() }),
    responses: { 200: z.array(plannerResourceSchema), 500: errorResponseSchema },
    summary: 'Get team members as planner resources',
  },

  getEvents: {
    method: 'GET',
    path: '/events',
    query: z.object({
      start: z.string().datetime({ message: 'start must be a valid ISO 8601 datetime' }),
      end: z.string().datetime({ message: 'end must be a valid ISO 8601 datetime' }),
      teamId: z.string().optional(),
      userId: z.string().optional(),
    }),
    responses: {
      200: z.array(plannerEventSchema),
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get planner events in date range',
  },

  createEvent: {
    method: 'POST',
    path: '/events',
    body: createPlannerEventBodySchema,
    responses: { 201: plannerEventSchema, 400: errorResponseSchema, 500: errorResponseSchema },
    summary: 'Create a planner event',
  },

  updateEvent: {
    method: 'PATCH',
    path: '/events/:id',
    pathParams: z.object({ id: z.coerce.number().int().positive() }),
    body: updatePlannerEventBodySchema,
    responses: {
      200: plannerEventSchema,
      403: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Update a planner event (drag-drop, resize, edit)',
  },

  deleteEvent: {
    method: 'DELETE',
    path: '/events/:id',
    pathParams: z.object({ id: z.coerce.number().int().positive() }),
    body: c.noBody(),
    responses: {
      200: z.object({ success: z.boolean() }),
      403: errorResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Delete a planner event',
  },

  validateEvent: {
    method: 'POST',
    path: '/validate',
    body: validatePlannerEventBodySchema,
    responses: {
      200: validatePlannerEventResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Check for scheduling conflicts',
  },
});

export type PlannerResource = z.infer<typeof plannerResourceSchema>;
export type PlannerEvent = z.infer<typeof plannerEventSchema>;
export type CreatePlannerEventBody = z.infer<typeof createPlannerEventBodySchema>;
export type UpdatePlannerEventBody = z.infer<typeof updatePlannerEventBodySchema>;
export type ValidatePlannerEventBody = z.infer<typeof validatePlannerEventBodySchema>;
export type ValidatePlannerEventResponse = z.infer<typeof validatePlannerEventResponseSchema>;
